<template>
  <section class="claim__item">
    <div class="claim__content">
      <h3 class="portal__title title--m">{{ getUiString(['claim_details', 'details', 'my_claim', 'claimnumber']) }}: {{claim.ReferenceNumber}}</h3>
      <div class="flight-info flight-info__row">
        <div class="flight-info__section flight-info__section--dates">
          <lennoc-icon image="calendar"/>
          <p class="date">{{ claimDepartureDate }}</p>
          <p class="flightnumber">{{ claimFlightCode }}</p>
        </div>
        <div class="flight-info__section flight-indo__section--departure">
          <lennoc-icon image="airplane-take-off"/>
          <p class="airport-name">{{ claimDepartureAirport }}</p>
          <p class="time">{{ claimDepartureFlightDateTime }}</p>
        </div>
        <div class="flight-info__section flight-info__section--arrival">
          <lennoc-icon image="airplane-landing"/>
          <p class="airport-name">{{ claimArrivalAirport }}</p>
          <p class="time">{{ claimArrivalFlightDateTime }}</p>
        </div>
      </div>
      <div class="claim-status claim-status__row divider divider--top">
        <div class="claim-status__section">
          <p class="label">{{ getUiString(['claim_list', 'flight_issue']) }}</p>
          <p class="value">{{ claimIncidentType }}</p>
        </div>
        <div v-if="claim.passengers.length" class="claim-status__section">
          <p class="label">{{ getUiString(['claim_list', 'claimer']) }}</p>
          <passenger-list
            :passengers="claim.passengers"
            :visiblePassengers="3"
            :openText="getUiString(['claim_details', 'details', 'passengers', 'open_text'])"
            :closedText="getUiString(['claim_details', 'details', 'passengers', 'closed_text'])"></passenger-list>
        </div>
        <div class="claim-status__section">
          <p class="label">{{ getUiString(['claim_list', 'status']) }}</p>
          <p v-if="!claimStatusDisplay.problems" class="value">{{ claim.ClaimStatusTitle }}</p>
          <template v-if="claimStatusDisplay.problems">
            <a
            class="link"
            @click.prevent="showDetail(problem)"
            :disabled="busy"
            v-for="problem in claimStatusDisplay.problems"
            :key="problem"
            >
            {{ getClaimTask(problem) }}
            </a>
          </template>
        </div>
      </div>
      <span class="claim__type--box">
        <lennoc-icon :image="claimTypeIcon" :domClass="claimTypeIcon"/>
      </span>
      <div class="claim-status claim-status__row claim-status__bottom-0">
        <div class="claim-status__section claim-status__align-bottom">
          <button class="button button--action-lead" @click.prevent="showDetail(false)" :disabled="busy">{{ getUiString(['claim_list', 'button_text']) }}</button>
        </div>
        <div class="claim-status__section">
          <div v-if="leadGeneratorConsumenten">
            <div class="claim__client-lead">
             <p class="lead-label">{{ getUiString(['claim_list', 'consumentendbond_title']) }}</p>
             <lennoc-icon v-if="leadGenerator.image" :image="leadGenerator.image" domClass="icon--static"/>
            </div>
            <span :image="leadGenerator.image"/>
            <p class="lead-text">{{ getUiString(['claim_list', 'consumentendbond_text']) }}</p>
            <a class="lead-text" href="https://www.consumentenbond.nl/acties/vluchtclaimservice?CID=euclaim_faq_cbwordlid#veelgesteldevragen" target="_blank">{{ getUiString(['claim_list', 'consumentendbond_text_link']) }}</a>
            <button class="button button--lead" @click.prevent="gotoPageLink()">
              {{ getUiString(['claim_list', 'consumentendbond-button_text']) }}
            </button>
          </div>
          <div v-else-if="leadGenerator" class="claim__client claim__client__bottom">
            <span>{{ getUiString(['claim_list', 'affiliate']) }}</span>
            <lennoc-icon v-if="leadGenerator.image" :image="leadGenerator.image" domClass="icon--static"/>
            <span class="claim__client--no-logo" v-else>{{ leadGenerator.name }}</span>
          </div>
        </div>
      </div>
    </div>
    <lennoc-spinner v-if="busy" isFullscreen="false" />
  </section>
</template>

<script>
import PassengerList from '@/components/person/PassengerList.vue'
import { mapGetters } from 'vuex'
import { formatDate } from '@/utils/datetime'
import { getLeadGenerator } from '@/utils/leadgenerators'

export default {
  name: 'ClaimListItem',
  components: {
    PassengerList
  },
  props: {
    claim: {
      type: Object,
      default () {
        return {}
      }
    }
  },
  computed: {
    ...mapGetters({
      getUiString: 'uistrings/getUiString'
    }),
    claimTypeIcon () {
      switch (this.claim.ClaimType) {
        case 'Regulation_261_2004':
          // Reguliere flight claim
          return 'plane-flat'
        case 'Regulation_889_2002':
          // Bagage claim
          return 'baggage'
        case 'Regulation_889_2002_Flight':
          // Extra kosten claim
          return 'extra-costs'
        case 'Voucher_Flight':
          // Corona voucher claim
          return 'corona-voucher'
        default:
          return 'plane-flat'
      }
    },
    claimIncidentType () {
      const incType = this.claim.ClaimType === 'Voucher_Flight' ? 'cs_Voucher'
        : `cs_${this.claim.ClaimType}_${this.claim.IncidentType}`
      return this.getUiString(['claim_regulations', incType])
    },
    claimDepartureDate () {
      return this.claim.DepartureFlightDateTime
        ? formatDate(this.claim.DepartureFlightDateTime, 'date')
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_flight_date'])
    },
    claimFlightCode () {
      return this.claim.FlightCode
        ? this.claim.FlightCode
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_flight_code'])
    },
    claimDepartureAirport () {
      return this.claim.DepartureAirport === 'Unknown'
        ? this.getUiString(['claim_details', 'details', 'my_claim', 'no_departure_airport'])
        : this.claim.DepartureAirport
    },
    claimArrivalAirport () {
      return this.claim.ArrivalAirport === 'Unknown'
        ? this.getUiString(['claim_details', 'details', 'my_claim', 'no_arrival_airport'])
        : this.claim.ArrivalAirport
    },
    flightDateFormat () {
      return this.claim.ClaimType === 'Voucher_Flight' ? 'date' : 'datetime'
    },
    claimDepartureFlightDateTime () {
      return this.claim.DepartureFlightDateTime
        ? formatDate(this.claim.DepartureFlightDateTime, this.flightDateFormat)
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_departure_time'])
    },
    claimArrivalFlightDateTime () {
      return this.claim.ArrivalFlightDateTime
        ? formatDate(this.claim.ArrivalFlightDateTime, this.flightDateFormat)
        : this.getUiString(['claim_details', 'details', 'my_claim', 'no_arrival_time'])
    },
    leadGenerator () {
      if (this.claim.leadGenerator === 1 || this.claim.leadGenerator === 9) {
        // that's us!
        return null
      } else {
        const lead = getLeadGenerator(this.claim.leadGenerator)
        if (lead) {
          return { image: lead.image, name: lead.name }
        } else {
          return null
        }
      }
    },
    leadGeneratorConsumenten () {
      if (this.claim.leadGenerator === 5) {
        return true
      } else {
        return false
      }
    },
    claimStatusDisplay () {
      return {
        claimStatus: this.claim.ClaimStatusTitle,
        problems: this.claim.openTasks.length > 0 ? this.claim.openTasks : false
      }
    }
  },
  data () {
    return {
      busy: false,
      error: false
    }
  },
  methods: {
    showDetail (anchor) {
      this.busy = true
      this.error = false
      if (anchor) {
        this.$router.push(`/claim/${this.claim.Id}/status/${anchor}`)
      } else {
        this.$router.push(`/claim/${this.claim.Id}/status`)
      }
    },
    getClaimTask (problem) {
      if (problem === 'RequestClaim' && this.claim.ClaimType !== 'Regulation_261_2004') {
        problem = 'RequestClaim_261'
      }

      return this.getUiString(['claim_tasks', problem])
    },
    gotoPageLink () {
      window.open('https://www.consumentenbond.nl/wordlid/lidmaatschap?CID=euclaim_dossierclaim_cbwordlid', '_blank', 'noreferrer')
    }
  }
}

</script>
